import { useEffect, useRef, useState } from 'react'
import KeepersPanel from './KeepersPanel'
import { api } from '../lib/api'
import { getCachedUser } from '../lib/auth'
import { createVault, unlockVault, isUnlocked, lockVault, wrapForShare, unwrapShare } from '../lib/vault'
import { askSage } from '../lib/sagebus'
import type { Tree, Collaborator, Activity } from '../lib/types'

interface Props {
  tree: Tree
  role: 'owner' | 'editor' | 'viewer'
  onClose: () => void
  onChanged: () => void
}

export default function TreeSettings({ tree, role, onClose, onChanged }: Props) {
  const [tab, setTab] = useState<'collab' | 'vault' | 'gedcom' | 'activity' | 'insights' | 'trash'>('collab')
  const [trash, setTrash] = useState<Awaited<ReturnType<typeof api.listTrash>> | null>(null)
  const [insights, setInsights] = useState<{ total: number; withBirth: number; withPlace: number; withAvatar: number; living: number; surnames: [string, number][] } | null>(null)
  const [collabs, setCollabs] = useState<Collaborator[]>([])
  const [activity, setActivity] = useState<Activity[]>([])
  const [email, setEmail] = useState('')
  const [collabRole, setCollabRole] = useState<'viewer' | 'editor'>('viewer')
  const [pass, setPass] = useState('')
  const [vaultMsg, setVaultMsg] = useState('')
  const [gedMsg, setGedMsg] = useState('')
  const [gedDups, setGedDups] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [sharePin, setSharePin] = useState('')
  const [shareCode, setShareCode] = useState('')
  const [redeemCode, setRedeemCode] = useState('')
  const [redeemPin, setRedeemPin] = useState('')
  const gedRef = useRef<HTMLInputElement>(null)

  const [orgs, setOrgs] = useState<{ id: string; name: string; my_role?: string }[]>([])
  const [claims, setClaims] = useState<Awaited<ReturnType<typeof api.treeClaims>>['claims']>([])

  useEffect(() => {
    api.listCollaborators(tree.id).then(setCollabs).catch(() => {})
    api.listActivity(tree.id).then(setActivity).catch(() => {})
    api.listOrgs().then(o => setOrgs(o.filter(x => x.my_role === 'owner' || x.my_role === 'admin'))).catch(() => {})
    if (role === 'owner') api.treeClaims(tree.id).then(r => setClaims(r.claims)).catch(() => setClaims([]))
  }, [tree.id, role])

  const resolveClaim = async (id: string, action: 'approve' | 'decline') => {
    await api.resolveClaim(id, action)
    api.treeClaims(tree.id).then(r => setClaims(r.claims)).catch(() => {})
    api.listCollaborators(tree.id).then(setCollabs).catch(() => {})
    onChanged()
  }

  useEffect(() => {
    if (tab === 'trash' && role !== 'viewer') api.listTrash(tree.id).then(setTrash).catch(() => {})
    if (tab === 'insights' && !insights) {
      api.getTree(tree.id).then(g => {
        const ps = g.persons || []
        const counts = new Map<string, number>()
        for (const p of ps) {
          const s = (p.last_name || '').trim()
          if (s) counts.set(s, (counts.get(s) || 0) + 1)
        }
        setInsights({
          total: ps.length,
          withBirth: ps.filter(p => p.birth_date).length,
          withPlace: ps.filter(p => p.birth_place).length,
          withAvatar: ps.filter(p => p.avatar_media_id).length,
          living: ps.filter(p => p.is_living).length,
          surnames: [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8),
        })
      }).catch(() => {})
    }
  }, [tab, tree.id, role, insights])

  const restore = async (kind: 'person' | 'media', id: string) => {
    await api.restoreFromTrash(tree.id, kind, id)
    setTrash(await api.listTrash(tree.id))
    onChanged()
  }

  const [purgeMsg, setPurgeMsg] = useState('')
  const myUserId = getCachedUser()?.id
  const fmtMB = (b: number) => (b >= 1048576 ? `${Math.round(b / 1048576)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`)

  /**
   * Permanent, and the only way to get the space back before the 30 days elapse.
   * Until v0.26.0 there was no such action at all: "free up some space" pointed at
   * a Trash that could not give any back (D-146).
   */
  const purge = async (id?: string, label?: string) => {
    const what = id ? `“${label}”` : 'every photo and file here that you can remove'
    if (!confirm(`Delete ${what} for good? This cannot be undone — restoring will no longer be possible.`)) return
    try {
      const r = await api.purgeTrash(tree.id, id)
      setPurgeMsg(
        r.failed
          // reported rather than hidden: those rows are still here on purpose
          ? `Removed ${r.purged}, but ${r.failed} could not be deleted and are still listed — the nightly sweep will try again.`
          : r.purged ? `Gone for good — ${fmtMB(r.bytes)} freed.` : 'Nothing here was yours to remove.')
      setTrash(await api.listTrash(tree.id))
      onChanged()
    } catch (e) {
      setPurgeMsg(e instanceof Error ? e.message.replace(/^API \d+: /, '').replace(/[{}"]|error:/g, '').trim() : 'Could not delete that')
    }
  }

  const assignCircle = async (orgId: string) => {
    await api.updateTree(tree.id, { org_id: orgId || null } as Parameters<typeof api.updateTree>[1])
    onChanged()
  }
  const setCircleRole = async (r: string) => {
    await api.updateTree(tree.id, { org_default_role: r } as Parameters<typeof api.updateTree>[1])
    onChanged()
  }

  const invite = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    try {
      await api.addCollaborator(tree.id, email.trim(), collabRole)
      setEmail('')
      setCollabs(await api.listCollaborators(tree.id))
    } finally { setBusy(false) }
  }

  const vaultAction = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true); setVaultMsg('')
    try {
      if (tree.vault_salt && tree.vault_check) {
        const ok = await unlockVault(tree.id, pass, tree.vault_salt, tree.vault_check)
        setVaultMsg(ok ? '🔓 Vault unlocked for this session.' : 'Wrong passphrase.')
        if (ok) onChanged()
      } else {
        const { vault_salt, vault_check } = await createVault(tree.id, pass)
        await api.updateTree(tree.id, { vault_salt, vault_check } as Parameters<typeof api.updateTree>[1])
        setVaultMsg('🔐 Vault created and unlocked. Store the passphrase safely — it cannot be recovered.')
        onChanged()
      }
      setPass('')
    } finally { setBusy(false) }
  }

  const importGed = async (file: File) => {
    setBusy(true); setGedMsg(''); setGedDups([])
    try {
      const text = await file.text()
      const res = await api.importGedcom(tree.id, text)
      const dups = (res as { possible_duplicates?: string[] }).possible_duplicates || []
      setGedDups(dups)
      setGedMsg(`Imported ${res.imported_persons} people and ${res.imported_relationships} bonds.` +
        (dups.length ? ` ⚠ Possible duplicates of existing members: ${dups.slice(0, 6).join(', ')}${dups.length > 6 ? '…' : ''}` : ''))
      onChanged()
    } catch (e) {
      setGedMsg(e instanceof Error ? e.message : 'Import failed')
    } finally { setBusy(false) }
  }

  const makeShare = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true); setVaultMsg('')
    try {
      const wrapped = await wrapForShare(tree.id, sharePin)
      const res = await api.createVaultShare({ tree_id: tree.id, ...wrapped })
      setShareCode(res.id)
      setSharePin('')
      setVaultMsg('Share created. Tell your kin the CODE and the PIN separately — the server holds only ciphertext.')
    } catch (err) {
      setVaultMsg(err instanceof Error ? err.message : 'Share failed')
    } finally { setBusy(false) }
  }

  const redeemShare = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true); setVaultMsg('')
    try {
      const blob = await api.redeemVaultShare(redeemCode.trim())
      const passphrase = await unwrapShare(redeemPin, blob.ciphertext, blob.iv, blob.salt)
      if (!passphrase) { setVaultMsg('Wrong PIN — the share code is now burned; ask for a new one.'); return }
      if (tree.vault_salt && tree.vault_check) {
        const ok = await unlockVault(tree.id, passphrase, tree.vault_salt, tree.vault_check)
        setVaultMsg(ok ? '🔓 Vault unlocked via shared key.' : 'Share decrypted but did not match this vault.')
        if (ok) { setRedeemCode(''); setRedeemPin(''); onChanged() }
      }
    } catch (err) {
      setVaultMsg(err instanceof Error ? err.message.replace(/^API \d+: /, '') : 'Redeem failed')
    } finally { setBusy(false) }
  }

  const unlocked = isUnlocked(tree.id)

  return (
    <div className="fixed inset-0 z-50 bg-void-950/85 backdrop-blur-sm flex items-center justify-center px-4 py-6 overflow-y-auto" onClick={onClose}>
      <div className="card-glass !bg-void-900/95 w-full max-w-xl agent-enter my-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 pt-4">
          <h2 className="font-display text-lg font-bold text-slate-100">{tree.name} — settings</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300 px-1">✕</button>
        </div>
        <div className="flex gap-1 px-5 mt-3 border-b border-white/[0.07]">
          {([['collab', 'Kin & Access'], ['vault', '🔐 Vault'], ['gedcom', 'GEDCOM'], ['insights', 'Insights'], ['trash', 'Trash'], ['activity', 'Activity']] as const).map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-3 py-2 text-sm rounded-t-lg ${tab === k ? 'bg-white/[0.07] text-gold-300' : 'text-slate-500 hover:text-slate-300'}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="p-5 min-h-[280px]">
          {tab === 'collab' && (
            <div>
              <KeepersPanel treeId={tree.id} treeName={tree.name} />
              {role === 'owner' && (
                <div className="card-glass p-4 mb-4">
                  <h4 className="font-display text-sm font-semibold text-slate-100 mb-1">🕊️ What Sage may see</h4>
                  <p className="text-[11px] text-slate-500 mb-3">
                    Sage's answers are produced by an outside AI service, so whatever it may see leaves this
                    archive. This is your call, per tree — and changes are recorded in the activity log.
                  </p>
                  <div className="space-y-1.5">
                    {([
                      ['deceased_only', 'Ancestors only (recommended)', 'The departed in full. Living relatives appear as a name and their place in the family — never exact dates or places.'],
                      ['full', 'Everything', 'Living relatives too, including exact birth dates and places. Choose this only if you are comfortable with it leaving the archive.'],
                      ['off', 'Nothing', 'Sage cannot see this tree at all. It can still explain how the app works.'],
                    ] as const).map(([val, label, blurb]) => (
                      <label key={val} className={`flex items-start gap-2.5 px-3 py-2 rounded-xl border cursor-pointer transition-colors ${
                        (tree.sage_scope || 'deceased_only') === val ? 'border-gold-500/40 bg-gold-500/[0.07]' : 'border-white/[0.07] bg-white/[0.02] hover:border-white/20'}`}>
                        <input type="radio" name="sage_scope" className="accent-gold-500 mt-0.5"
                          checked={(tree.sage_scope || 'deceased_only') === val}
                          onChange={async () => { await api.updateTree(tree.id, { sage_scope: val }); onChanged() }} />
                        <span>
                          <span className="text-sm text-slate-200 block">{label}</span>
                          <span className="text-[11px] text-slate-500">{blurb}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {role === 'owner' && claims.length > 0 && (
                <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.05] p-3.5 mb-4">
                  <p className="text-sm text-slate-200 mb-2">✋ Identity requests awaiting your blessing</p>
                  <div className="space-y-1.5">
                    {claims.map(c => (
                      <div key={c.id} className="flex items-center justify-between gap-2 px-2.5 py-2 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                        <div className="min-w-0">
                          <p className="text-xs text-slate-200 truncate">
                            <b>{c.requester_name || c.requester_email}</b>
                            {c.kind === 'claim' ? <> says <b>{c.person_name}</b> is them</>
                              : c.kind === 'link' ? <> proposes <b>{c.person_name}</b> ↔ {c.target_name || c.target_email}</>
                              : <> asks to join this tree</>}
                          </p>
                          {c.note && <p className="text-[10px] text-slate-500 truncate">“{c.note}”</p>}
                        </div>
                        <div className="flex gap-1.5 flex-shrink-0">
                          <button onClick={() => resolveClaim(c.id, 'approve')} className="text-[11px] px-2 py-1 rounded-lg border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10">Approve</button>
                          <button onClick={() => resolveClaim(c.id, 'decline')} className="text-[11px] px-2 py-1 rounded-lg border border-red-500/25 text-red-400/90 hover:bg-red-500/10">Decline</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {role === 'owner' && (
                <div className="rounded-xl border border-rootv-500/25 bg-rootv-600/[0.06] p-3.5 mb-4">
                  <p className="text-sm text-slate-200 mb-2">🫂 Family Circle</p>
                  <div className="flex gap-2 flex-wrap items-center">
                    <select value={tree.org_id || ''} onChange={e => assignCircle(e.target.value)} className="input-dark !py-2 text-sm flex-1 min-w-[160px]">
                      <option value="">Personal (no circle)</option>
                      {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                    {tree.org_id && (
                      <select value={tree.org_default_role || 'viewer'} onChange={e => setCircleRole(e.target.value)} className="input-dark !py-2 text-sm w-40">
                        <option value="viewer">members view</option>
                        <option value="editor">members edit</option>
                      </select>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-600 mt-2">Circle admins always get edit + manage. Direct invites below can raise individual members higher.</p>
                </div>
              )}
              {role === 'owner' && (
                <form onSubmit={invite} className="flex gap-2 mb-4">
                  <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="relative@example.com" className="input-dark !py-2 text-sm flex-1" />
                  <select value={collabRole} onChange={e => setCollabRole(e.target.value as 'viewer' | 'editor')}
                    className="input-dark !py-2 text-sm w-28">
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                  </select>
                  <button type="submit" disabled={busy} className="btn-gold !py-2 text-sm">Invite</button>
                </form>
              )}
              <div className="space-y-2">
                {collabs.map(c => (
                  <div key={c.id} className="flex items-center justify-between px-3 py-2 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                    <div className="min-w-0">
                      <p className="text-sm text-slate-200 truncate">{c.user_name || c.email}</p>
                      <p className="text-[11px] text-slate-500">{c.role} · {c.joined ? 'joined' : 'invited — will connect when they sign up'}</p>
                    </div>
                    {role === 'owner' && (
                      <button onClick={async () => { await api.removeCollaborator(c.id); setCollabs(await api.listCollaborators(tree.id)) }}
                        className="text-xs text-red-400/70 hover:text-red-400">Remove</button>
                    )}
                  </div>
                ))}
                {collabs.length === 0 && <p className="text-sm text-slate-500 text-center py-6">No collaborators yet — this constellation is yours alone.</p>}
              </div>
              <p className="text-[10px] text-slate-600 mt-3">Invitees sign in with their email at family-ancestry.istayintech.com; access binds automatically. Admin approval still applies to new accounts.</p>
            </div>
          )}

          {tab === 'vault' && (
            <div>
              <p className="text-sm text-slate-400 mb-1">
                The <span className="text-gold-300">Legacy Vault</span> gives this tree true end-to-end encryption for chosen photos, videos, and documents.
              </p>
              <p className="text-[11px] text-slate-600 mb-4">
                AES-256-GCM, key derived on your device (PBKDF2 · 310k rounds). The server stores only ciphertext — nobody without the passphrase, including us, can open vault items. Lose the passphrase, lose the vault. Share it only with trusted kin.
              </p>
              {tree.vault_salt ? (
                unlocked ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between card-glass p-4">
                      <p className="text-sm text-emerald-300">🔓 Vault is unlocked on this device</p>
                      <button onClick={() => { lockVault(tree.id); onChanged() }} className="btn-ghost !py-1.5 !px-3 text-xs">Lock</button>
                    </div>
                    {role === 'owner' && (
                      <div className="card-glass p-4">
                        <p className="text-sm text-slate-200 mb-1">Share vault access</p>
                        <p className="text-[11px] text-slate-600 mb-3">
                          Wraps the passphrase under a one-time PIN on THIS device. Give a collaborator the code and the PIN through different channels; the share burns after one use and expires in 48h.
                        </p>
                        <form onSubmit={makeShare} className="flex gap-2">
                          <input type="password" required minLength={4} value={sharePin} onChange={e => setSharePin(e.target.value)}
                            placeholder="One-time PIN (min 4)" className="input-dark !py-2 text-sm flex-1" />
                          <button type="submit" disabled={busy || sharePin.length < 4} className="btn-gold !py-2 text-sm">Create share</button>
                        </form>
                        {shareCode && (
                          <p className="text-xs text-gold-300 mt-2">Share code: <span className="font-mono text-base tracking-widest">{shareCode}</span></p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <form onSubmit={vaultAction} className="flex gap-2">
                      <input type="password" required value={pass} onChange={e => setPass(e.target.value)}
                        placeholder="Vault passphrase" className="input-dark !py-2 text-sm flex-1" />
                      <button type="submit" disabled={busy || !pass} className="btn-gold !py-2 text-sm">{busy ? '…' : 'Unlock'}</button>
                    </form>
                    <div className="card-glass p-4">
                      <p className="text-sm text-slate-200 mb-2">Have a share code from the owner?</p>
                      <form onSubmit={redeemShare} className="flex gap-2 flex-wrap">
                        <input required value={redeemCode} onChange={e => setRedeemCode(e.target.value)}
                          placeholder="Share code" className="input-dark !py-2 text-sm flex-1 min-w-[120px] font-mono" />
                        <input type="password" required value={redeemPin} onChange={e => setRedeemPin(e.target.value)}
                          placeholder="PIN" className="input-dark !py-2 text-sm w-24" />
                        <button type="submit" disabled={busy || !redeemCode || !redeemPin} className="btn-ghost !py-2 text-sm">Redeem</button>
                      </form>
                    </div>
                  </div>
                )
              ) : role === 'owner' ? (
                <form onSubmit={vaultAction} className="flex gap-2">
                  <input type="password" required minLength={8} value={pass} onChange={e => setPass(e.target.value)}
                    placeholder="Choose a strong passphrase (min 8 chars)" className="input-dark !py-2 text-sm flex-1" />
                  <button type="submit" disabled={busy || pass.length < 8} className="btn-gold !py-2 text-sm">{busy ? '…' : 'Create Vault'}</button>
                </form>
              ) : (
                <p className="text-sm text-slate-500">Only the tree owner can initialize the vault.</p>
              )}
              {vaultMsg && <p className="text-xs text-gold-300 mt-3">{vaultMsg}</p>}
            </div>
          )}

          {tab === 'gedcom' && (
            <div className="space-y-4">
              <div className="card-glass p-4">
                <p className="text-sm text-slate-200 mb-2">Export</p>
                <p className="text-xs text-slate-500 mb-3">Download this tree as GEDCOM 5.5.1 — importable into Ancestry, FamilySearch, Gramps, and every serious genealogy tool.</p>
                <a href={api.exportGedcomUrl(tree.id)} download className="btn-gold !py-2 text-sm inline-block">⬇ Download .ged</a>
              </div>
              {role !== 'viewer' && (
                <div className="card-glass p-4">
                  <p className="text-sm text-slate-200 mb-2">Import</p>
                  <p className="text-xs text-slate-500 mb-3">Bring an existing GEDCOM in — people and family bonds are added to this tree.</p>
                  <button onClick={() => gedRef.current?.click()} disabled={busy} className="btn-ghost !py-2 text-sm">
                    {busy ? 'Importing…' : '⬆ Import .ged file'}
                  </button>
                  <input ref={gedRef} type="file" accept=".ged,.gedcom,text/plain" className="hidden"
                    onChange={e => { const f = e.target.files?.[0]; if (f) importGed(f); e.target.value = '' }} />
                </div>
              )}
              {gedMsg && <p className="text-xs text-gold-300">{gedMsg}</p>}
              {gedDups.length > 0 && (
                <button onClick={() => { askSage(`The GEDCOM import flagged possible duplicate people in this tree: ${gedDups.join(', ')}. For each, tell me whether the entries look like the same person and which one to keep or merge, using the tree context.`); onClose() }}
                  className="btn-gold !py-2 text-sm">✳ Review duplicates with Sage</button>
              )}
            </div>
          )}

          {tab === 'insights' && (
            <div className="space-y-4">
              {insights ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {([
                      ['Souls', insights.total, ''],
                      ['Birth dates', insights.withBirth, `${insights.total ? Math.round((insights.withBirth / insights.total) * 100) : 0}%`],
                      ['Birth places', insights.withPlace, `${insights.total ? Math.round((insights.withPlace / insights.total) * 100) : 0}%`],
                      ['Portraits', insights.withAvatar, `${insights.total ? Math.round((insights.withAvatar / insights.total) * 100) : 0}%`],
                    ] as const).map(([label, n, pct]) => (
                      <div key={label} className="card-glass p-3 text-center">
                        <p className="text-xl font-display font-bold text-gold-300">{n}</p>
                        <p className="text-[10px] text-slate-500">{label}{pct ? ` · ${pct}` : ''}</p>
                      </div>
                    ))}
                  </div>
                  {insights.total > 0 && (
                    <div className="space-y-1.5">
                      {([['Dates recorded', insights.withBirth], ['Places recorded', insights.withPlace], ['Portraits added', insights.withAvatar]] as const).map(([label, n]) => (
                        <div key={label}>
                          <div className="flex justify-between text-[10px] text-slate-500 mb-0.5"><span>{label}</span><span>{n}/{insights.total}</span></div>
                          <div className="h-1 rounded-full bg-white/[0.07] overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-emerald-500/70 to-gold-500/70" style={{ width: `${(n / insights.total) * 100}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {insights.surnames.length > 0 && (
                    <div>
                      <p className="text-xs text-slate-400 mb-2">Family lines</p>
                      <div className="flex flex-wrap gap-1.5">
                        {insights.surnames.map(([s, n]) => (
                          <span key={s} className="text-xs px-2.5 py-1 rounded-lg bg-white/[0.04] border border-white/[0.08] text-slate-300">
                            {s} <span className="text-slate-600">×{n}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="text-[11px] text-slate-600">Gaps here are Sage's favorite prompts — ask “what's missing in this tree?”</p>
                </>
              ) : <p className="text-sm text-slate-500 text-center py-6">Reading the constellation…</p>}
            </div>
          )}

          {tab === 'trash' && (
            <div className="space-y-3">
              {role === 'viewer' ? (
                <p className="text-sm text-slate-500 text-center py-6">Editors and owners manage the trash.</p>
              ) : trash ? (
                <>
                  <p className="text-[11px] text-slate-500">
                    Removed people &amp; memories rest here for {trash.retention_days} days, then fade forever.
                    Until they do, photos and videos still count against your storage — deleting is not the same as freeing space.
                  </p>
                  {trash.purgeable_count > 0 && (
                    <div className="flex items-center gap-2.5 px-3 py-2 rounded-xl border border-amber-500/30 bg-amber-500/[0.06]">
                      <span className="text-sm">♻</span>
                      <p className="flex-1 text-xs text-amber-200">
                        {/* what you may destroy, and what that gives BACK to you, are
                            different numbers on a shared tree — the quota is per-uploader */}
                        <b>{fmtMB(trash.purgeable_bytes)}</b> of photos and files here you can delete for good
                        {trash.reclaimable_bytes !== trash.purgeable_bytes && (
                          <> — <b>{fmtMB(trash.reclaimable_bytes)}</b> of it counts against your own storage</>
                        )}
                        . People in the trash are not touched.
                      </p>
                      <button onClick={() => purge()} className="text-xs px-2.5 py-1 rounded-lg border border-red-500/40 text-red-300 hover:bg-red-500/10 whitespace-nowrap">Empty files</button>
                    </div>
                  )}
                  {purgeMsg && <p className="text-[11px] text-gold-300">{purgeMsg}</p>}
                  {trash.persons.map(p => (
                    <div key={p.id} className="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                      <span className="text-sm">🕯</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-200 truncate">{p.first_name} {p.last_name}</p>
                        <p className="text-[10px] text-slate-600">removed {p.deleted_at.slice(0, 16).replace('T', ' ')}</p>
                      </div>
                      <button onClick={() => restore('person', p.id)} className="text-xs px-2.5 py-1 rounded-lg border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10">Restore</button>
                    </div>
                  ))}
                  {trash.media.map(m => (
                    <div key={m.id} className="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                      <span className="text-sm">{m.kind === 'video' ? '🎞' : m.kind === 'document' ? '📜' : '🖼'}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-200 truncate">{m.filename}</p>
                        <p className="text-[10px] text-slate-600">
                        removed {m.deleted_at.slice(0, 16).replace('T', ' ')}{m.size_bytes ? ` · ${fmtMB(m.size_bytes)}` : ''}
                      </p>
                      </div>
                      <button onClick={() => restore('media', m.id)} className="text-xs px-2.5 py-1 rounded-lg border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10">Restore</button>
                      {(role === 'owner' || m.uploader_id === myUserId) && (
                        <button onClick={() => purge(m.id, m.filename)} title="Delete for good and free the space now"
                          className="text-xs px-2.5 py-1 rounded-lg border border-red-500/30 text-red-300/80 hover:bg-red-500/10 whitespace-nowrap">Delete for good</button>
                      )}
                    </div>
                  ))}
                  {trash.persons.length === 0 && trash.media.length === 0 && (
                    <p className="text-sm text-slate-500 text-center py-6">The trash is empty — nothing waits in the dark.</p>
                  )}
                </>
              ) : <p className="text-sm text-slate-500 text-center py-6">Opening the vault of the departed…</p>}
            </div>
          )}

          {tab === 'activity' && (
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {activity.map(a => (
                <div key={a.id} className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg bg-white/[0.02]">
                  <span className="w-1.5 h-1.5 rounded-full bg-gold-400 flex-shrink-0" />
                  <p className="text-xs text-slate-300 flex-1 min-w-0 truncate">
                    <span className="text-slate-100">{a.who}</span> {a.action}{a.subject ? ` — ${a.subject}` : ''}
                  </p>
                  <span className="text-[10px] text-slate-600 flex-shrink-0">{a.created_at.slice(5, 16)}</span>
                </div>
              ))}
              {activity.length === 0 && <p className="text-sm text-slate-500 text-center py-6">No activity recorded yet.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
